import React, { useContext, useState } from 'react'
import { FieldArray, Formik, FormikProvider, useFormik } from 'formik';
import * as Yup from "yup";
import { Alert, Button, Card, Col, Container, Form, Row } from 'react-bootstrap';
import { ArrowLeft, Edit, Edit2, Edit3, Loader, MinusCircle, PlusCircle } from 'react-feather';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom'
import ErrorAlert from '../../components/ErrorAlert';
import CustomSelect from '../../components/CustomSelect';
import { faker } from '@faker-js/faker';
import NotyfContext from '../../contexts/NotyfContext';
import { useCreateCompanyMutation } from '../../redux/slices/companiesSlice';
import { useGetCapabilitiesQuery } from '../../redux/slices/capabilitiesSlice';
import { useGetIndustriesQuery } from '../../redux/slices/industriesSlice';
import { Capability } from '../../types/capability';
import { Industry } from '../../types/industry';
import { useGetCategoryQuery } from '../../redux/slices/anzsicSlice';
import { Category } from '../../types/anzsic';

const CompanyCreate = () => {

    const navigate = useNavigate();
    const notyf = useContext(NotyfContext);

    const { data: capabilities, isLoading: isLoadingCapabilities, isSuccess: isSuccessCapabilities, isError: isErrorCapabilities, error: errorCapabilities } = useGetCapabilitiesQuery(undefined, {
        refetchOnMountOrArgChange: true,
    })

    const { data: industries, isLoading: isLoadingIndustries, isSuccess: isSuccessIndustries, isError: isErrorIndustries, error: errorIndustries } = useGetIndustriesQuery(undefined, {
        refetchOnMountOrArgChange: true,
    })

    const { data: anzsics, isLoading: isLoadingAnzsics, isSuccess: isSuccessAnzsics, isError: isErrorAnzsics, error: errorAnzsics } = useGetCategoryQuery(undefined, {
        refetchOnMountOrArgChange: true,
    })

    const [createCompany, { isLoading: isLoadingCreateCompany, isSuccess: isSuccessCreateCompany, isError: isErrorCreateCompany, error: errorCreateCompany }] = useCreateCompanyMutation();

    const yupSchema = Yup.object().shape({
        name: Yup.string(),
        website: Yup.string()
            .matches(
                /((https?):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/,
                'Enter correct url!'
            )
            .required('Please enter website'),
        mefMember: Yup.bool().optional(),
        anzsic: Yup.array().optional(),
        capabilities: Yup.array().optional(),
        postcode: Yup.string().optional(),
        industries: Yup.array().optional(),
        products: Yup.object().shape({
            name: Yup.string(),
            sites: Yup.array(),
            tangible: Yup.bool(),
            verified: Yup.bool(),
        }).optional(),
        services: Yup.object().shape({
            name: Yup.string(),
            sites: Yup.array(),
            tangible: Yup.bool(),
            verified: Yup.bool(),
        }).optional()
    })



    const formik = useFormik({
        initialValues: {
            name: '',
            tradingAs: '',
            website: '',
            keyPrincipal: '',
            revenue: 0,
            mefMember: false,
            category: [''],
            categories: [''],
            capabilities: [''],
            postcode: '',
            industries: [''],
            subIndustries: [''],
            products: [{
                name: '',
                sites: '',
                tangible: true,
                verified: false,
            }],
            services: [{
                name: '',
                sites: '',
                tangible: false,
                verified: false,
            }],
            submit: false
        },
        enableReinitialize: true,
        // validationSchema: yupSchema,
        onSubmit: async (values, { setErrors, setStatus, setSubmitting }) => {
            try {
                console.log('submit!', values);

                let response = createCompany({
                    name: values.name,
                    website: values.website,
                    mefMember: values.mefMember,
                    tradingAs: values.tradingAs,
                    keyPrincipal: values.keyPrincipal,
                    revenue: Number(values.revenue),
                    //@ts-ignore
                    category: values.category,
                    //@ts-ignore
                    capabilities: values.capabilities,
                    postcode: values.postcode,
                    //@ts-ignore
                    industries: values.industries,
                    //@ts-ignore
                    subIndustries: values.subIndustries,
                    //@ts-ignore
                    products: values.products,
                    //@ts-ignore
                    services: values.services,
                    //@ts-ignore
                    verified: false,
                }).unwrap();

                console.log('response', response);

            } catch (error: any) {
                console.log('error', error);
                const message = error.response ? error.response.data.message : error.error ? error.error : 'Something went wrong'

                setStatus({ success: false })
                setErrors({ submit: message })
                setSubmitting(false);
            }
        }
    })

    let content;

    if (isSuccessCreateCompany) {
        notyf.open({
            message: 'Success Creating Company!',
            type: 'success',
            duration: 2000,
            ripple: true,
            dismissible: true,
            position: {
                x: 'right',
                y: 'top',
            }
        })
        navigate(`/`)
    }

    if (isLoadingCreateCompany || isLoadingCapabilities || isLoadingIndustries || isLoadingAnzsics) {
        content = (
            <React.Fragment>
                <Helmet title="Loading..." />
                <Container fluid className="p-0">
                    <Loader />
                </Container>
            </React.Fragment>
        )
    } else if (isErrorCreateCompany || isErrorCapabilities || isErrorIndustries || isErrorAnzsics) {
        content = (
            <React.Fragment>
                <Container fluid className="p-0">
                    {isErrorCapabilities ? (
                        <ErrorAlert title="Error loading capabilities!" error={JSON.stringify(errorCapabilities, null, 2)} />
                    ) : isErrorCreateCompany ? (
                        <ErrorAlert title="Error creating Companies!" error={JSON.stringify(errorCreateCompany, null, 2)} />
                    ) : isErrorIndustries ? (

                        <ErrorAlert title="Error loading Industries!" error={JSON.stringify(errorIndustries, null, 2)} />
                    ) : isErrorAnzsics ? (
                        <ErrorAlert title="Error loading Anzsics!" error={JSON.stringify(errorAnzsics, null, 2)} />
                    ) : ''}
                </Container>
            </React.Fragment>
        )
    } else if (isSuccessCapabilities && capabilities && isSuccessAnzsics && anzsics && isSuccessIndustries && industries) {

        let capabilityOptions: { label: string; value: string; }[] = [];

        capabilities && capabilities.map((cap: Capability) => {
            capabilityOptions.push({ label: cap.name, value: cap._id ? cap._id : '' })
        })

        // console.log('industries', industries);

        let industryOptions: { label: string; value: string; }[] = [];

        industries && industries.filter((indust: Industry) => indust.sub !== true).map((industry: Industry) => {
            industryOptions.push({ label: industry.name, value: industry._id });
        })

        let subIndustryOptions: { label: string; value: string; }[] = [];

        industries && industries.filter((indust: Industry) => indust.sub === true).map((industry: Industry) => {
            subIndustryOptions.push({ label: industry.name, value: industry._id });
        })

        let anzsicOptions: { label: string; value: string; }[] = [];
        anzsics && anzsics.map((anzsic: Category) => {
            anzsicOptions.push({ label: anzsic.name, value: anzsic._id })
        })

        content = (
            <React.Fragment>
                <Helmet title={`Create Company`} />
                <Container fluid className="p-0">
                    <Row>
                        <Col>
                            <Card>

                                <Card>
                                    <Row className='mb-2 mb-xl-3'>
                                        <Col xs="auto" className="d-none d-sm-block">
                                            <Card.Header>
                                                <Card.Title>Create Company</Card.Title>
                                            </Card.Header>
                                        </Col>
                                        <Col xs="auto" className="ms-auto text-end">
                                            <Card.Header>
                                                <Row>
                                                    {/* <Col>
                                                    <Button variant="warning" onClick={() => navigate(`/Companiess/${CompaniesId}/edit`)}><Edit2 size={18} /></Button>
                                                </Col> */}
                                                    <Col>
                                                        <Button variant="secondary" onClick={() => navigate(`/`)}><ArrowLeft color="white" size={18} /></Button>
                                                    </Col>
                                                </Row>
                                            </Card.Header>
                                        </Col>
                                    </Row>
                                    <Card.Body>
                                        <Form onSubmit={formik.handleSubmit}>
                                            {formik.errors.submit && (
                                                <Alert className="my-3" variant="danger">
                                                    <div className="alert-message">{formik.errors.submit}</div>
                                                </Alert>
                                            )}
                                            {isErrorCreateCompany && (
                                                <Alert className="my-3" variant="danger">
                                                    <div className="alert-message">{JSON.stringify(errorCreateCompany, null, 2)}</div>
                                                </Alert>
                                            )}
                                            <Card.Body>


                                                <Row className="mb-3">
                                                    <Form.Group className="mb-3" as={Col} md={2}>
                                                        <Form.Label>Company Name</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            size="lg"
                                                            value={formik.values.name}
                                                            onChange={formik.handleChange}
                                                            onBlur={formik.handleBlur}
                                                            name="name"
                                                            placeholder="Enter Company Name"
                                                            isInvalid={Boolean(formik.touched.name && formik.errors.name)}
                                                        />
                                                        {!!formik.touched.name && (
                                                            <Form.Control.Feedback type="invalid">
                                                                {formik.errors.name}
                                                            </Form.Control.Feedback>
                                                        )}
                                                    </Form.Group>
                                                    <Form.Group className="mb-3" as={Col} md={2}>
                                                        <Form.Label>Trading As</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            size="lg"
                                                            value={formik.values.tradingAs}
                                                            onChange={formik.handleChange}
                                                            onBlur={formik.handleBlur}
                                                            name="tradingAs"
                                                            placeholder="Enter Trading As"
                                                            isInvalid={Boolean(formik.touched.tradingAs && formik.errors.tradingAs)}
                                                        />
                                                        {!!formik.touched.name && (
                                                            <Form.Control.Feedback type="invalid">
                                                                {formik.errors.name}
                                                            </Form.Control.Feedback>
                                                        )}
                                                    </Form.Group>
                                                    <Form.Group className="mb-3" as={Col} md={2}>
                                                        <Form.Label>Company Website</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            size="lg"
                                                            value={formik.values.website}
                                                            onChange={formik.handleChange}
                                                            onBlur={formik.handleBlur}
                                                            name="website"
                                                            placeholder="Enter Company website"
                                                            isInvalid={Boolean(formik.touched.website && formik.errors.website)}
                                                        />
                                                        {!!formik.touched.website && (
                                                            <Form.Control.Feedback type="invalid">
                                                                {formik.errors.website}
                                                            </Form.Control.Feedback>
                                                        )}
                                                    </Form.Group>
                                                    <Form.Group className="mb-3" as={Col} md={2}>
                                                        <Form.Label>Key Principal</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            size="lg"
                                                            value={formik.values.keyPrincipal}
                                                            onChange={formik.handleChange}
                                                            onBlur={formik.handleBlur}
                                                            name="keyPrincipal"
                                                            placeholder="Enter Company Key Principal"
                                                            isInvalid={Boolean(formik.touched.keyPrincipal && formik.errors.keyPrincipal)}
                                                        />
                                                        {!!formik.touched.website && (
                                                            <Form.Control.Feedback type="invalid">
                                                                {formik.errors.website}
                                                            </Form.Control.Feedback>
                                                        )}
                                                    </Form.Group>
                                                    <Form.Group className="mb-3" as={Col} md={2}>
                                                        <Form.Label>Revenue</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            size="lg"
                                                            value={formik.values.revenue}
                                                            onChange={formik.handleChange}
                                                            onBlur={formik.handleBlur}
                                                            name="revenue"
                                                            placeholder="Enter Est. Revenue"
                                                            isInvalid={Boolean(formik.touched.revenue && formik.errors.revenue)}
                                                        />
                                                        {!!formik.touched.website && (
                                                            <Form.Control.Feedback type="invalid">
                                                                {formik.errors.website}
                                                            </Form.Control.Feedback>
                                                        )}
                                                    </Form.Group>
                                                </Row>
                                                <Row>
                                                    <Form.Group className="mb-3" as={Col} md={2}>
                                                        <Form.Label>
                                                            Post Code
                                                        </Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="postcode"
                                                            value={formik.values.postcode}
                                                            onBlur={formik.handleBlur}
                                                            onChange={formik.handleChange}
                                                            size="lg"
                                                            placeholder="Enter Post Code"
                                                            isInvalid={Boolean(formik.touched.postcode && formik.errors.postcode)}
                                                        />
                                                        {!!formik.touched.postcode && (
                                                            <Form.Control.Feedback type="invalid">
                                                                {formik.errors.postcode}
                                                            </Form.Control.Feedback>
                                                        )}
                                                    </Form.Group>
                                                    <Form.Group className="mb-3" as={Col} md={2}>
                                                        <Form.Label>MEF Member?</Form.Label>
                                                        <Form.Check
                                                            type="switch"
                                                            id="mefMemberSwitch"
                                                            name="mefMember"
                                                            label={String(formik.values.mefMember)}
                                                            // value={String(formik.values.mefMember)}
                                                            checked={formik.values.mefMember}
                                                            onChange={formik.handleChange}
                                                            onBlur={formik.handleBlur}
                                                            isInvalid={Boolean(formik.touched.mefMember && formik.errors.mefMember)}
                                                        />
                                                        {
                                                            !!formik.touched.mefMember && (
                                                                <Form.Control.Feedback type="invalid">
                                                                    {formik.errors.mefMember}
                                                                </Form.Control.Feedback>
                                                            )
                                                        }
                                                    </Form.Group>
                                                </Row>
                                                <Row>
                                                    <Form.Group className="mb-3" as={Col} md={3}>
                                                        <Form.Label>
                                                            Categories
                                                        </Form.Label>
                                                        <CustomSelect
                                                            className="react-select-container"
                                                            //@ts-ignore
                                                            onChange={value => value.forEach((val, index) => formik.setFieldValue(`category[${index}]`, val.value))}
                                                            value={formik.values.category}
                                                            options={anzsicOptions}
                                                            multiSelect={true}
                                                        />
                                                        {!!formik.touched?.category && (
                                                            <div style={{
                                                                display: 'block',
                                                                width: '100%',
                                                                marginTop: '0.25rem',
                                                                fontSize: '80%',
                                                                color: '#d9534f'
                                                            }}>
                                                                {formik.errors?.category}
                                                            </div>
                                                        )}
                                                    </Form.Group>
                                                    <Form.Group className="mb-3" as={Col} md={3}>
                                                        <Form.Label>
                                                            Capabilities
                                                        </Form.Label>
                                                        <CustomSelect
                                                            className="react-select-container"
                                                            //@ts-ignore
                                                            onChange={value => value.forEach((val, index) => formik.setFieldValue(`capabilities[${index}]`, val.value))}
                                                            value={formik.values.capabilities}
                                                            options={capabilityOptions}
                                                            multiSelect={true}
                                                        />
                                                        {!!formik.touched?.capabilities && (
                                                            <div style={{
                                                                display: 'block',
                                                                width: '100%',
                                                                marginTop: '0.25rem',
                                                                fontSize: '80%',
                                                                color: '#d9534f'
                                                            }}>
                                                                {formik.errors?.capabilities}
                                                            </div>
                                                        )}
                                                    </Form.Group>
                                                    <Form.Group className="mb-3" as={Col} md={3}>
                                                        <Form.Label>
                                                            Industries
                                                        </Form.Label>
                                                        <CustomSelect
                                                            className="react-select-container"
                                                            //@ts-ignore
                                                            onChange={value => value.forEach((val, index) => formik.setFieldValue(`industries[${index}]`, val.value))}
                                                            value={formik.values.industries}
                                                            options={industryOptions}
                                                            multiSelect={true}
                                                        />
                                                        {!!formik.touched?.industries && (
                                                            <div style={{
                                                                display: 'block',
                                                                width: '100%',
                                                                marginTop: '0.25rem',
                                                                fontSize: '80%',
                                                                color: '#d9534f'
                                                            }}>
                                                                {formik.errors?.industries}
                                                            </div>
                                                        )}
                                                    </Form.Group>
                                                    <Form.Group className="mb-3" as={Col} md={3}>
                                                        <Form.Label>
                                                            Sub-Industries
                                                        </Form.Label>
                                                        <CustomSelect
                                                            className="react-select-container"
                                                            //@ts-ignore
                                                            onChange={value => value.forEach((val, index) => formik.setFieldValue(`subIndustries[${index}]`, val.value))}
                                                            value={formik.values.subIndustries}
                                                            options={subIndustryOptions}
                                                            multiSelect={true}
                                                        />
                                                        {!!formik.touched?.subIndustries && (
                                                            <div style={{
                                                                display: 'block',
                                                                width: '100%',
                                                                marginTop: '0.25rem',
                                                                fontSize: '80%',
                                                                color: '#d9534f'
                                                            }}>
                                                                {formik.errors?.subIndustries}
                                                            </div>
                                                        )}
                                                    </Form.Group>
                                                </Row>
                                            </Card.Body>
                                            <Row>
                                                <Col>
                                                    <Card>
                                                        <Card.Header>
                                                            <Row>
                                                                <Col>
                                                                    <Card.Title>Products</Card.Title>
                                                                </Col>
                                                                <Col xs="auto" className="ms-auto text-end mt-n1">

                                                                </Col>
                                                            </Row>
                                                        </Card.Header>
                                                        <Card.Body>
                                                            <Card>
                                                                <Card.Body>
                                                                    <FormikProvider value={formik}>
                                                                        {/* @ts-ignore */}
                                                                        <FieldArray
                                                                            name="products"
                                                                            render={(arrayHelpers) => (
                                                                                <>
                                                                                    {formik.values.products.map((product, prodIndex) => (
                                                                                        <div key={prodIndex}>
                                                                                            <Row>
                                                                                                <Col md={10}>
                                                                                                    <Form.Group className="mb-3" as={Col} md={12}>
                                                                                                        <Form.Label>Product Name</Form.Label>
                                                                                                        <Form.Control
                                                                                                            type="text"
                                                                                                            name={`products[${prodIndex}].name`}
                                                                                                            placeholder="Enter Product Name"
                                                                                                            value={formik.values.products[prodIndex].name}
                                                                                                            onChange={formik.handleChange}
                                                                                                        />
                                                                                                    </Form.Group>
                                                                                                    <Form.Group className="mb-3" as={Col} md={12}>
                                                                                                        <Form.Label>Sites</Form.Label>
                                                                                                        <Form.Control
                                                                                                            type="text"
                                                                                                            name={`products[${prodIndex}].sites`}
                                                                                                            placeholder="Enter Sites"
                                                                                                            value={formik.values.products[prodIndex].sites}
                                                                                                            onChange={formik.handleChange}
                                                                                                        />
                                                                                                    </Form.Group>
                                                                                                </Col>
                                                                                                <Col md={2}>
                                                                                                    <Button size="sm" variant="outline-none" onClick={() => arrayHelpers.remove(prodIndex)}><MinusCircle size={18} /></Button>
                                                                                                </Col>
                                                                                            </Row>
                                                                                            <Row>
                                                                                                <Col>
                                                                                                    <Form.Group className="mb-3" as={Col} md={12}>
                                                                                                        <Form.Label>Tangible?</Form.Label>
                                                                                                        <Form.Check
                                                                                                            type="switch"
                                                                                                            id={`products[${prodIndex}].tangibleSwitch`}
                                                                                                            name={`products[${prodIndex}].tangible`}
                                                                                                            label={String(formik.values.products[prodIndex].tangible)}
                                                                                                            // value={String(formik.values.mefMember)}
                                                                                                            checked={formik.values.products[prodIndex].tangible}
                                                                                                            onChange={formik.handleChange}
                                                                                                            onBlur={formik.handleBlur}
                                                                                                        // isInvalid={Boolean(formik.touched?.products[prodIndex]?.tangible && formik.errors?.products[prodIndex]?.tangible)}
                                                                                                        />
                                                                                                        {/* {
                                                                                                !!formik.touched?.products[prodIndex]?.tangible && (
                                                                                                    <Form.Control.Feedback type="invalid">
                                                                                                        {formik.errors?.products[prodIndex]?.tangible}
                                                                                                    </Form.Control.Feedback>
                                                                                                )
                                                                                            } */}
                                                                                                    </Form.Group>
                                                                                                </Col>
                                                                                                <Col>
                                                                                                    <Form.Group className="mb-3" as={Col} md={12}>
                                                                                                        <Form.Label>Verified</Form.Label>
                                                                                                        <Form.Check
                                                                                                            type="switch"
                                                                                                            id={`products[${prodIndex}].verifiedSwitch`}
                                                                                                            name={`products[${prodIndex}].verified`}
                                                                                                            label={String(formik.values.products[prodIndex].verified)}
                                                                                                            // value={String(formik.values.mefMember)}
                                                                                                            checked={formik.values.products[prodIndex].verified}
                                                                                                            onChange={formik.handleChange}
                                                                                                            onBlur={formik.handleBlur}
                                                                                                        // isInvalid={Boolean(formik.touched?.products[prodIndex]?.tangible && formik.errors?.products[prodIndex]?.tangible)}
                                                                                                        />
                                                                                                        {/* {
                                                                                                !!formik.touched?.products[prodIndex]?.tangible && (
                                                                                                    <Form.Control.Feedback type="invalid">
                                                                                                        {formik.errors?.products[prodIndex]?.tangible}
                                                                                                    </Form.Control.Feedback>
                                                                                                )
                                                                                            } */}
                                                                                                    </Form.Group>
                                                                                                </Col>
                                                                                            </Row>
                                                                                        </div>
                                                                                    ))}
                                                                                    <Button variant="outline-primary" className="btn-pill me-1 mb-1" size="sm" onClick={() => arrayHelpers.push({
                                                                                        name: '',
                                                                                        sites: [''],
                                                                                        verified: false,
                                                                                        tangible: false,
                                                                                    })}>
                                                                                        <PlusCircle size={20} />
                                                                                    </Button>
                                                                                </>
                                                                            )}
                                                                        >
                                                                        </FieldArray>
                                                                    </FormikProvider>
                                                                </Card.Body>
                                                            </Card>
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                                <Col>
                                                    <Card>
                                                        <Card.Header>
                                                            <Row>
                                                                <Col>
                                                                    <Card.Title>Services</Card.Title>
                                                                </Col>
                                                                <Col xs="auto" className="ms-auto text-end mt-n1">
                                                                </Col>
                                                            </Row>
                                                        </Card.Header>
                                                        <Card.Body>
                                                            <Card>
                                                                <Card.Body>
                                                                    <FormikProvider value={formik}>
                                                                        {/* @ts-ignore */}
                                                                        <FieldArray
                                                                            name="services"
                                                                            render={(arrayHelpers) => (
                                                                                <>
                                                                                    {formik.values.services.map((service, serviceIndex) => (
                                                                                        <div key={serviceIndex}>
                                                                                            <Row>
                                                                                                <Col md={10}>
                                                                                                    <Form.Group className="mb-3" as={Col} md={12}>
                                                                                                        <Form.Label>Service Name</Form.Label>
                                                                                                        <Form.Control
                                                                                                            type="text"
                                                                                                            name={`services[${serviceIndex}].name`}
                                                                                                            placeholder="Enter Service Name"
                                                                                                            value={formik.values.services[serviceIndex].name}
                                                                                                            onChange={formik.handleChange}
                                                                                                        />
                                                                                                    </Form.Group>
                                                                                                    <Form.Group className="mb-3" as={Col} md={12}>
                                                                                                        <Form.Label>Sites</Form.Label>
                                                                                                        <Form.Control
                                                                                                            type="text"
                                                                                                            name={`services[${serviceIndex}].sites`}
                                                                                                            placeholder="Enter Sites"
                                                                                                            value={formik.values.services[serviceIndex].sites}
                                                                                                            onChange={formik.handleChange}
                                                                                                        />
                                                                                                    </Form.Group>
                                                                                                </Col>
                                                                                                <Col md={2}>
                                                                                                    <Button size="sm" variant="outline-none" onClick={() => arrayHelpers.remove(serviceIndex)}><MinusCircle size={18} /></Button>
                                                                                                </Col>
                                                                                            </Row>
                                                                                            <Row>
                                                                                                <Col>
                                                                                                    <Form.Group className="mb-3" as={Col} md={12}>
                                                                                                        <Form.Label>Tangible?</Form.Label>
                                                                                                        <Form.Check
                                                                                                            type="switch"
                                                                                                            id={`services[${serviceIndex}].tangibleSwitch`}
                                                                                                            name={`services[${serviceIndex}].tangible`}
                                                                                                            label={String(formik.values.services[serviceIndex].tangible)}
                                                                                                            // value={String(formik.values.mefMember)}
                                                                                                            checked={formik.values.services[serviceIndex].tangible}
                                                                                                            onChange={formik.handleChange}
                                                                                                            onBlur={formik.handleBlur}
                                                                                                        // isInvalid={Boolean(formik.touched?.services[prodIndex]?.tangible && formik.errors?.services[prodIndex]?.tangible)}
                                                                                                        />
                                                                                                        {/* {
                                                                                                !!formik.touched?.services[prodIndex]?.tangible && (
                                                                                                    <Form.Control.Feedback type="invalid">
                                                                                                        {formik.errors?.services[prodIndex]?.tangible}
                                                                                                    </Form.Control.Feedback>
                                                                                                )
                                                                                            } */}
                                                                                                    </Form.Group>
                                                                                                </Col>
                                                                                                <Col>
                                                                                                    <Form.Group className="mb-3" as={Col} md={12}>
                                                                                                        <Form.Label>Verified</Form.Label>
                                                                                                        <Form.Check
                                                                                                            type="switch"
                                                                                                            id={`services[${serviceIndex}].verifiedSwitch`}
                                                                                                            name={`services[${serviceIndex}].verified`}
                                                                                                            label={String(formik.values.services[serviceIndex].verified)}
                                                                                                            // value={String(formik.values.mefMember)}
                                                                                                            checked={formik.values.services[serviceIndex].verified}
                                                                                                            onChange={formik.handleChange}
                                                                                                            onBlur={formik.handleBlur}
                                                                                                        // isInvalid={Boolean(formik.touched?.products[prodIndex]?.tangible && formik.errors?.products[prodIndex]?.tangible)}
                                                                                                        />
                                                                                                        {/* {
                                                                                                !!formik.touched?.products[prodIndex]?.tangible && (
                                                                                                    <Form.Control.Feedback type="invalid">
                                                                                                        {formik.errors?.products[prodIndex]?.tangible}
                                                                                                    </Form.Control.Feedback>
                                                                                                )
                                                                                            } */}
                                                                                                    </Form.Group>
                                                                                                </Col>
                                                                                            </Row>
                                                                                        </div>
                                                                                    ))}
                                                                                    <Button variant="outline-primary" className="btn-pill me-1 mb-1" size="sm" onClick={() => arrayHelpers.push({
                                                                                        name: '',
                                                                                        sites: [''],
                                                                                        verified: false,
                                                                                        tangible: false,
                                                                                    })}>
                                                                                        <PlusCircle size={20} />
                                                                                    </Button>
                                                                                </>
                                                                            )}
                                                                        >
                                                                        </FieldArray>
                                                                    </FormikProvider>
                                                                </Card.Body>
                                                            </Card>
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                            </Row>

                                            {isLoadingCreateCompany ? (
                                                <Loader />
                                            ) : (
                                                <div className="mt-3 text-center">
                                                    <Button
                                                        className="me-1 mb-1"
                                                        type="submit"
                                                        variant="outline-danger"
                                                        size="lg"
                                                        disabled={formik.isSubmitting}
                                                        onClick={() => navigate(`/`)}
                                                    >
                                                        Cancel
                                                    </Button>
                                                    <Button
                                                        className="me-1 mb-1"
                                                        type="submit"
                                                        variant="primary"
                                                        size="lg"
                                                        disabled={formik.isSubmitting}
                                                    >
                                                        Save
                                                    </Button>
                                                </div>

                                            )}
                                        </Form>
                                    </Card.Body>
                                </Card>
                            </Card>
                        </Col>
                    </Row>
                </Container >
            </React.Fragment >
        )
    } else {
        content = (
            <React.Fragment>
                <Helmet title="Companies Loading.." />
                <Container fluid className="p-0">
                    <Loader />
                </Container>
            </React.Fragment>
        )
    }

    return content;
}

export default CompanyCreate